import { useRef, useEffect, useState, RefObject } from 'react';

interface UseTextLineBreakReturn {
    textRef: RefObject<HTMLDivElement | null>;
    textLines: string[];
}

/**
 * Hook that breaks text into multiple lines based on a maximum width constraint.
 * Measures text using Canvas API with actual font properties from the referenced DOM element.
 *
 * @param text - The text to break into lines
 * @param maxWidth - Maximum width as a CSS value (e.g., '50vw', '300px', '20rem', 'calc(100vw - 200px)')
 * @returns Object containing:
 *   - textRef: Ref to attach to the text container element (required for font measurement)
 *   - textLines: Array of text strings, each fitting within maxWidth
 *
 * @example
 * const { textRef, textLines } = useTextLineBreak('Long text here', '50vw');
 * return (
 *   <div ref={textRef}>
 *     {textLines.map((line, i) => <div key={i}>{line}</div>)}
 *   </div>
 * );
 */
export const useTextLineBreak = (text: string, maxWidth = '50vw'): UseTextLineBreakReturn => {
    const textRef = useRef<HTMLDivElement>(null);
    const [textLines, setTextLines] = useState<string[]>([text]);

    useEffect(() => {
        if (!textRef.current || !text) return;

        const element = textRef.current;
        const computedStyle = window.getComputedStyle(element);
        const fontSize = computedStyle.fontSize;
        const fontFamily = computedStyle.fontFamily;

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) return;

        context.font = `${fontSize} ${fontFamily}`;

        // Create a temporary element to compute the CSS width value
        const tempDiv = document.createElement('div');
        tempDiv.style.width = maxWidth;
        tempDiv.style.position = 'absolute';
        tempDiv.style.visibility = 'hidden';
        document.body.appendChild(tempDiv);
        const maxWidthPx = tempDiv.offsetWidth;
        document.body.removeChild(tempDiv);

        const words = text.split(' ');
        const lines: string[] = [];
        let currentLine = '';

        for (const word of words) {
            const testLine = currentLine ? `${currentLine} ${word}` : word;
            const metrics = context.measureText(testLine);

            if (metrics.width > maxWidthPx && currentLine) {
                lines.push(currentLine);
                currentLine = word;
            } else {
                currentLine = testLine;
            }
        }

        if (currentLine) {
            lines.push(currentLine);
        }

        setTextLines(lines);
    }, [text, maxWidth]);

    return { textRef, textLines };
};
